// Offline layer: caches GET responses, queues offline mutations (IndexedDB) and replays them when back online.
import axios from 'axios';
import { toast } from 'sonner';
import { idbGet, idbSet, idbDel, idbClear, idbEntries } from './idb';
import { isQueueable, applyOfflineMutation } from './patchers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_CACHE_ENTRIES = 600;
const NO_CACHE_PATTERNS = [/\/pdf(\?|$)/, /\/download/, /\/export/, /\/auth\/login/, /\/auth\/logout/, /\/telemetry/, /\/preview/];

const memCache = new Map(); // path -> { data, ts }
let queue = []; // { id, method, path, body, tempId, createsId, ts, status: 'pending'|'failed', error, label }
let idMap = {}; // tempId -> real id
let installed = false;
let lastNeedsNetworkToast = 0;

const state = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  pending: 0,
  failed: 0,
  syncing: false,
  lastSync: null,
  hydrated: false,
};
const listeners = new Set();
const emit = () => {
  state.pending = queue.filter((q) => q.status === 'pending').length;
  state.failed = queue.filter((q) => q.status === 'failed').length;
  listeners.forEach((fn) => { try { fn({ ...state, failedItems: queue.filter((q) => q.status === 'failed') }); } catch (_) {} });
};

export const offlineStore = {
  subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
  getSnapshot: () => ({ ...state, failedItems: queue.filter((q) => q.status === 'failed') }),
};

// ---------- helpers ----------
const clone = (v) => (v === undefined ? v : JSON.parse(JSON.stringify(v)));

const toPath = (url) => {
  if (!url || typeof url !== 'string' || !url.startsWith(API)) return null;
  const rest = url.slice(API.length);
  const [p, qs] = rest.split('?');
  if (!qs) return p;
  const params = new URLSearchParams(qs);
  params.delete('token');
  const s = params.toString();
  return s ? `${p}?${s}` : p;
};

const parseBody = (d) => {
  if (d === undefined || d === null) return undefined;
  if (typeof d === 'string') { try { return JSON.parse(d); } catch (_) { return d; } }
  return d;
};

const persistQueue = () => idbSet('meta', 'queue', queue).catch(() => {});
const persistIdMap = () => idbSet('meta', 'idMap', idMap).catch(() => {});

const cacheApi = {
  get: async (k) => memCache.get(k)?.data,
  set: async (k, data) => {
    memCache.set(k, { data, ts: Date.now() });
    await idbSet('cache', k, { data, ts: Date.now() }).catch(() => {});
  },
  del: async (k) => { memCache.delete(k); await idbDel('cache', k).catch(() => {}); },
  keys: () => Array.from(memCache.keys()),
};

const cacheNetworkResponse = (path, data) => {
  if (memCache.size >= MAX_CACHE_ENTRIES) {
    const oldest = Array.from(memCache.entries()).sort((a, b) => a[1].ts - b[1].ts).slice(0, 50);
    oldest.forEach(([k]) => { memCache.delete(k); idbDel('cache', k).catch(() => {}); });
  }
  memCache.set(path, { data, ts: Date.now() });
  idbSet('cache', path, { data, ts: Date.now() }).catch(() => {});
};

const cachedResponse = (path, config) => ({
  data: clone(memCache.get(path).data),
  status: 200,
  statusText: 'OK (offline cache)',
  headers: { 'x-offline-cache': '1', 'x-offline-cache-ts': String(memCache.get(path).ts) },
  config,
  request: {},
});

const setOnline = (v) => {
  if (state.online === v) return;
  state.online = v;
  emit();
  if (v) sync();
};

const notifyNeedsNetwork = () => {
  const now = Date.now();
  if (now - lastNeedsNetworkToast < 3000) return;
  lastNeedsNetworkToast = now;
  toast.error('Hors ligne : cette action nécessite une connexion internet', { duration: 4000 });
};

const describe = (method, path, body) => {
  const b = body || {};
  if (path === '/projets') return `Nouveau dossier ${b.ville || ''}`.trim();
  if (/^\/projets\/[^/]+$/.test(path)) return 'Modification dossier';
  if (path === '/devis-maag' || path === '/devis-maag/duplicate') return `Nouveau devis « ${b.objet || ''} »`;
  if (/^\/devis-maag\/[^/]+\/lots/.test(path)) return 'Devis : lots';
  if (/^\/devis-maag\/[^/?]+$/.test(path)) return 'Devis : informations';
  if (/postes/.test(path) || /^\/postes\//.test(path)) return `Devis : ${method === 'delete' ? 'suppression' : method === 'put' ? 'modification' : 'ajout'} prestation${b.designation || b.description ? ` (${(b.designation || b.description).slice(0, 40)})` : ''}`;
  if (/fournisseurs-lots/.test(path)) return `Marge : ${b.entreprise || 'slot fournisseur'}${b.montant_ht !== undefined ? ` ${b.montant_ht} €` : ''}`;
  if (path === '/enseignes') return `Nouvelle enseigne ${b.nom || ''}`;
  return `${method.toUpperCase()} ${path}`;
};

const enqueueAndRespond = async (method, path, config) => {
  const body = parseBody(config.data);
  const tempId = `tmp-${crypto.randomUUID()}`;
  const data = await applyOfflineMutation({ method, path, body, tempId, cache: cacheApi });
  queue.push({
    id: crypto.randomUUID(), method, path, body, tempId,
    createsId: method === 'post' && data && data.id === tempId,
    ts: Date.now(), status: 'pending', label: describe(method, path, body),
  });
  await persistQueue();
  emit();
  return { data: clone(data), status: 200, statusText: 'OK (offline queued)', headers: { 'x-offline-queued': '1' }, config, request: {} };
};

const isNetworkError = (error) => !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || !navigator.onLine);

// ---------- hydration ----------
let readyResolve;
const ready = new Promise((r) => { readyResolve = r; });

const hydrate = async () => {
  try {
    const entries = await idbEntries('cache');
    entries.forEach(([k, v]) => { if (v && v.data !== undefined) memCache.set(k, v); });
    queue = (await idbGet('meta', 'queue')) || [];
    idMap = (await idbGet('meta', 'idMap')) || {};
  } catch (e) {
    console.warn('[offline] hydrate failed', e);
  }
  state.hydrated = true;
  emit();
  readyResolve();
};

// ---------- sync / replay ----------
const substitute = (s) => Object.entries(idMap).reduce((acc, [t, r]) => acc.split(t).join(r), s);

// Id réel créé par le serveur : à la racine, ou imbriqué (ex. postes-enriched renvoie { poste: {...}, ... })
const extractCreatedId = (data) => {
  if (!data || typeof data !== 'object') return null;
  if (typeof data.id === 'string') return data.id;
  for (const k of ['poste', 'devis', 'projet', 'fournisseur', 'lot', 'item', 'data']) {
    if (data[k] && typeof data[k].id === 'string') return data[k].id;
  }
  return null;
};

export const sync = async () => {
  await ready;
  if (state.syncing || !navigator.onLine || !queue.some((q) => q.status === 'pending')) return;
  if (!localStorage.getItem('token')) return;
  state.syncing = true;
  emit();
  let replayed = 0;
  for (const item of queue.filter((q) => q.status === 'pending')) {
    const path = substitute(item.path);
    const bodyStr = item.body !== undefined ? substitute(JSON.stringify(item.body)) : undefined;
    if (/tmp-[0-9a-f-]{36}/.test(path + (bodyStr || ''))) {
      item.status = 'failed';
      item.error = "L'élément d'origine n'a pas pu être identifié sur le serveur (il existe peut-être déjà) : vérifiez-le dans l'application puis abandonnez cette entrée.";
      item.orphan = true;
      await persistQueue(); emit();
      continue;
    }
    try {
      const res = await axios.request({
        method: item.method, url: `${API}${path}`, data: bodyStr !== undefined ? JSON.parse(bodyStr) : undefined,
        headers: bodyStr !== undefined ? { 'Content-Type': 'application/json' } : {}, __replay: true,
      });
      if (item.createsId) {
        const realId = extractCreatedId(res.data);
        if (realId) { idMap[item.tempId] = realId; await persistIdMap(); }
      }
      queue = queue.filter((q) => q.id !== item.id);
      replayed += 1;
    } catch (e) {
      if (!e.response || e.response.status === 401) { setOnlineSilently(!!e.response); break; }
      item.status = 'failed';
      const d = e.response.data?.detail;
      item.error = typeof d === 'string' ? d : `Erreur ${e.response.status}`;
    }
    await persistQueue(); emit();
  }
  state.syncing = false;
  if (replayed) state.lastSync = Date.now();
  emit();
  if (replayed) await afterSync(replayed);
};

const setOnlineSilently = (v) => { if (state.online !== v) { state.online = v; emit(); } };

const afterSync = async (replayed) => {
  memCache.clear();
  await idbClear('cache').catch(() => {});
  const failed = queue.filter((q) => q.status === 'failed').length;
  let url = window.location.pathname + window.location.search;
  Object.entries(idMap).forEach(([t, r]) => { url = url.split(t).join(r); });
  toast.success(`Synchronisé ✓ ${replayed} modification${replayed > 1 ? 's' : ''} enregistrée${replayed > 1 ? 's' : ''}${failed ? ` — ${failed} en erreur (voir bandeau)` : ''}. Actualisation…`, { duration: 2500 });
  window.dispatchEvent(new CustomEvent('maag:offline-synced', { detail: { replayed, failed } }));
  setTimeout(() => { window.location.replace(url); }, 1600);
};

export const retryFailed = async () => {
  queue.forEach((q) => { if (q.status === 'failed') { q.status = 'pending'; q.error = null; } });
  await persistQueue(); emit();
  sync();
};

export const discardItem = async (id) => {
  queue = queue.filter((q) => q.id !== id);
  await persistQueue(); emit();
};

export const discardAllFailed = async () => {
  queue = queue.filter((q) => q.status !== 'failed');
  await persistQueue(); emit();
};

export const hardRefresh = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    memCache.clear();
    await idbClear('cache').catch(() => {});
  } catch (e) {
    console.warn('[offline] hardRefresh', e);
  }
  const u = new URL(window.location.href);
  u.searchParams.set('_r', String(Date.now()));
  window.location.replace(u.toString());
};

// ---------- install ----------
export const installOfflineLayer = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  hydrate();

  axios.interceptors.request.use(async (config) => {
    if (config.__replay) return config;
    const path = toPath(config.url);
    if (!path) return config;
    await ready;
    const method = (config.method || 'get').toLowerCase();
    if (method === 'get' || method === 'head' || method === 'options') {
      if (!navigator.onLine && memCache.has(path)) config.adapter = () => Promise.resolve(cachedResponse(path, config));
      return config;
    }
    if (!navigator.onLine) {
      if (isQueueable(method, path) && !(config.data instanceof FormData)) {
        config.adapter = () => enqueueAndRespond(method, path, config);
        return config;
      }
      notifyNeedsNetwork();
      const err = new axios.AxiosError('Hors ligne : action impossible sans connexion', 'ERR_OFFLINE', config);
      err.isOffline = true;
      throw err;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const cfg = response.config || {};
      const path = toPath(cfg.url);
      const fromCache = !!(response.headers && (response.headers['x-offline-cache'] || response.headers['x-offline-queued']));
      if (!fromCache) setOnline(true);
      if (
        path && !fromCache && (cfg.method || 'get').toLowerCase() === 'get' && response.status === 200 &&
        !cfg.responseType && !NO_CACHE_PATTERNS.some((re) => re.test(path)) && response.data !== undefined && typeof response.data !== 'string'
      ) {
        cacheNetworkResponse(path, response.data);
      }
      return response;
    },
    async (error) => {
      const cfg = error?.config;
      if (!cfg || cfg.__replay || error.isOffline) throw error;
      const path = toPath(cfg.url);
      if (!path || !isNetworkError(error)) throw error;
      setOnline(false);
      const method = (cfg.method || 'get').toLowerCase();
      if (method === 'get') {
        if (memCache.has(path)) return cachedResponse(path, cfg);
        throw error;
      }
      if (isQueueable(method, path) && !(cfg.data instanceof FormData)) return enqueueAndRespond(method, path, cfg);
      notifyNeedsNetwork();
      throw error;
    }
  );

  window.addEventListener('online', () => { setOnline(true); sync(); });
  window.addEventListener('offline', () => setOnline(false));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') sync(); });
  window.addEventListener('focus', () => sync());
  setInterval(() => { if (navigator.onLine) sync(); }, 20000);
  ready.then(() => sync());
};
